import type { TerminationConfigForTooltip } from '../types'
import { getFormattedDate } from '../utils'

/**
 * Appends enrollment window dates to a status tooltip if an enrollment event exists.
 *
 * It returns an html string that should be sanitized and used as the content of
 * a tooltip.
 *
 * @param statusTip - The base status tooltip text
 * @param enrollmentEvent - Optional enrollment event containing window dates
 * @returns An html string that should be sanitized and used as the content of a tooltip
 */
export const getStatusTipWithEnrollmentWindow = (
  statusTip: string,
  enrollmentEvent?: { enrollment_window_start_date: string; enrollment_window_end_date: string } | null
): string => {
  if (enrollmentEvent == null) {
    return statusTip
  }

  const startDate = getFormattedDate(enrollmentEvent.enrollment_window_start_date)
  const endDate = getFormattedDate(enrollmentEvent.enrollment_window_end_date)

  return `${statusTip}<br><br>Enrollment window: ${startDate} - ${endDate}`
}

/**
 * Appends termination config details to a status tooltip if a termination config exists.
 *
 * It returns an html string that should be sanitized and used as the content of
 * a tooltip. Includes final date of coverage and COBRA eligibility.
 *
 * @param statusTip - The base status tooltip text
 * @param terminationConfig - Optional termination config with effective_date, cobra_eligible
 * @returns An html string that should be sanitized and used as the content of a tooltip
 */
export const getStatusTipWithTerminationConfig = (
  statusTip: string,
  terminationConfig?: TerminationConfigForTooltip | null
): string => {
  if (terminationConfig == null) {
    return statusTip
  }

  const parts: string[] = [statusTip]

  if (terminationConfig.effective_date != null && terminationConfig.effective_date !== '') {
    parts.push(`Their final day of coverage on Thatch is ${getFormattedDate(terminationConfig.effective_date)}.`)
  }

  if (terminationConfig.cobra_eligible !== undefined && terminationConfig.cobra_eligible !== null) {
    parts.push('<br><br>')
    parts.push(
      terminationConfig.cobra_eligible
        ? 'Employee is marked as COBRA eligible.'
        : 'Employee is marked as COBRA ineligible.'
    )
  }

  if (parts.length === 1) {
    return statusTip
  }

  return parts.join(' ')
}
